/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

/**
 * The shapes of work the PDF studio can do. Everything is described in PDF
 * space — points, origin at the page's bottom-left — so the same model drives
 * the on-screen overlay and the pdf-lib burn-in on save.
 */

/** A rectangle in PDF points. */
export interface PdfRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export type AnnotKind =
  | 'highlight'
  | 'underline'
  | 'strike'
  | 'textbox'
  | 'note'
  | 'ink'
  | 'rect'
  | 'ellipse'
  | 'line'
  | 'arrow'
  | 'stamp'
  | 'image'
  | 'cover' // white-out + replacement text — the "edit text" tool
  | 'redact';

/** One mark a person placed on a page. */
export interface PdfAnnot {
  id: string;
  /** 0-based page index. */
  page: number;
  kind: AnnotKind;
  /** Bounding box for most kinds; the first rect for multi-line markup. */
  x: number;
  y: number;
  w: number;
  h: number;
  /** Per-line boxes for highlight/underline/strike. */
  rects?: PdfRect[];
  /** Freehand strokes for `ink`. */
  points?: { x: number; y: number }[];
  text?: string;
  /** CSS colour, `#rrggbb`. */
  color?: string;
  /** Decoded pixels for `image` and the drawn signature. */
  image?: { bytes: Uint8Array; kind: 'png' | 'jpg'; el?: HTMLImageElement };
}

/** An AcroForm field to create on save. */
export interface PdfField {
  id: string;
  page: number;
  kind: 'text' | 'check' | 'radio' | 'dropdown';
  name: string;
  rect: PdfRect;
  /** Options for a dropdown, or the option value a radio button stands for. */
  options?: string[];
}

/** The pointer tools the toolbar and tools menu can arm. */
export type ToolId =
  | 'select'
  | 'pan'
  | 'highlight'
  | 'underline'
  | 'strike'
  | 'textbox'
  | 'note'
  | 'ink'
  | 'shape-rect'
  | 'shape-ellipse'
  | 'shape-line'
  | 'shape-arrow'
  | 'stamp'
  | 'edit-text'
  | 'add-text'
  | 'edit-image'
  | 'add-image'
  | 'sign'
  | 'sign-field'
  | 'sign-digital'
  | 'crop'
  | 'redact'
  | 'field-text'
  | 'field-check'
  | 'field-radio'
  | 'field-dropdown';

/** Tools that draw a new mark by dragging a rectangle on a page. */
export const RECT_TOOLS: ReadonlySet<ToolId> = new Set([
  'shape-rect',
  'shape-ellipse',
  'shape-line',
  'shape-arrow',
  'crop',
  'redact',
  'edit-image',
  'add-image',
  'field-text',
  'field-check',
  'field-radio',
  'field-dropdown'
]);

/** Tools that act on a single click. */
export const CLICK_TOOLS: ReadonlySet<ToolId> = new Set(['textbox', 'note', 'edit-text', 'add-text', 'stamp', 'sign', 'sign-field', 'sign-digital']);

/** What the click tools map onto. */
export const CLICK_KIND: Partial<Record<ToolId, AnnotKind>> = {
  textbox: 'textbox',
  note: 'note',
  'edit-text': 'cover',
  'add-text': 'textbox'
};

/** What the rectangle tools map onto. */
export const RECT_KIND: Partial<Record<ToolId, AnnotKind>> = {
  'shape-rect': 'rect',
  'shape-ellipse': 'ellipse',
  'shape-line': 'line',
  'shape-arrow': 'arrow',
  redact: 'redact'
};

/**
 * Presentation info for the armed-tool chip. `hint` selects the usage hint:
 * text = drag over text, click = click to place, box = drag a rectangle,
 * draw = freehand drag, field = drag a rectangle or click to place.
 */
export const TOOL_INFO: Partial<Record<ToolId, { icon: string; label: string; hint: 'text' | 'click' | 'box' | 'draw' | 'field' }>> = {
  highlight: { icon: 'border_color', label: 'HIGHLIGHT', hint: 'text' },
  underline: { icon: 'format_underlined', label: 'UNDERLINE', hint: 'text' },
  strike: { icon: 'strikethrough_s', label: 'STRIKE', hint: 'text' },
  textbox: { icon: 'text_fields', label: 'TEXTBOX', hint: 'click' },
  note: { icon: 'chat_bubble_outline', label: 'NOTE', hint: 'click' },
  ink: { icon: 'draw', label: 'DRAW', hint: 'draw' },
  'shape-rect': { icon: 'rectangle', label: 'SH_RECT', hint: 'box' },
  'shape-ellipse': { icon: 'circle', label: 'SH_ELLIPSE', hint: 'box' },
  'shape-line': { icon: 'remove', label: 'SH_LINE', hint: 'box' },
  'shape-arrow': { icon: 'arrow_forward', label: 'SH_ARROW', hint: 'box' },
  stamp: { icon: 'approval', label: 'STAMP', hint: 'click' },
  'edit-text': { icon: 'edit', label: 'EDIT_TEXT', hint: 'click' },
  'add-text': { icon: 'text_increase', label: 'ADD_TEXT', hint: 'click' },
  'edit-image': { icon: 'image', label: 'EDIT_IMAGE', hint: 'box' },
  'add-image': { icon: 'add_photo_alternate', label: 'ADD_IMAGE', hint: 'box' },
  sign: { icon: 'gesture', label: 'SIGN', hint: 'click' },
  'sign-field': { icon: 'draw', label: 'F_SIGN', hint: 'click' },
  'sign-digital': { icon: 'verified_user', label: 'SIGN_DIGITAL', hint: 'click' },
  crop: { icon: 'crop', label: 'P_CROP', hint: 'box' },
  redact: { icon: 'visibility_off', label: 'R_MARK', hint: 'box' },
  'field-text': { icon: 'text_fields', label: 'F_TEXT', hint: 'field' },
  'field-check': { icon: 'check_box', label: 'F_CHECK', hint: 'field' },
  'field-radio': { icon: 'radio_button_checked', label: 'F_RADIO', hint: 'field' },
  'field-dropdown': { icon: 'arrow_drop_down_circle', label: 'F_DROPDOWN', hint: 'field' }
};

let nextId = 1;

export function annotId(): string {
  return `a${nextId++}`;
}
